import { format } from 'date-fns';
import { Terminal, Inbox, BoomBox, AlertTriangle, Hash } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { JsonViewer } from '@/components/shared/JsonViewer';
import type { DeliveryLog, OutcomeEvent } from '@/types';
import { cn } from '@/lib/utils';

interface DeliveryLogDrawerProps {
  log: DeliveryLog | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function EmitRow({ event }: { event: OutcomeEvent }) {
  const target =
    event.kind === 'http' ? `${event.method} ${event.url}` : `${event.service} · ${event.method}`;
  const payload = event.kind === 'http' ? event.body : event.data;
  return (
    <li className="space-y-1 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant={event.kind === 'http' ? 'secondary' : 'outline'}
          className="font-mono text-xs"
        >
          {target}
        </Badge>
      </div>
      {payload !== undefined && (
        <JsonViewer data={payload} className="text-xs" />
      )}
    </li>
  );
}

export function DeliveryLogDrawer({ log, open, onOpenChange }: DeliveryLogDrawerProps) {
  if (!log) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-[680px] w-full sm:w-2/3 min-w-0 p-0 flex flex-col">
        <SheetHeader className="px-4 sm:px-6 py-4 border-b">
          <SheetTitle>Delivery log</SheetTitle>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Badge
              className={cn(
                'font-normal',
                log.outcome === 'success'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
              )}
            >
              {log.outcome}
            </Badge>
            <Badge variant="outline">{log.triggerKind}</Badge>
            <span className="font-mono text-xs text-muted-foreground">
              {log.correlationId}
            </span>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 px-4 sm:px-6 py-4">
            {/* Metadata */}
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
              <MetaField label="Event ID" value={log.eventId} mono />
              <MetaField label="Retry count" value={String(log.retryCount)} />
              <MetaField label="Total duration" value={`${log.totalDurationMs}ms`} />
              <MetaField label="Created" value={format(new Date(log.createdAt), 'MMM d, yyyy HH:mm:ss')} />
            </div>

            {/* Received payload */}
            <Section title="Received input" icon={<Inbox className="h-4 w-4" />}>
              <JsonViewer data={log.received} />
            </Section>

            {/* Sandbox */}
            <Section title="Sandbox execution" icon={<Terminal className="h-4 w-4" />}>
              <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
                <Badge variant={log.sandbox?.success ? 'success' : 'destructive'}>
                  {log.sandbox?.success ? 'Success' : 'Failed'}
                </Badge>
                <span className="text-muted-foreground">{log.sandbox?.durationMs ?? '—'}ms</span>
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <BoomBox className="h-3.5 w-3.5" /> {(log.sandbox?.emits ?? []).length} emits
                </span>
              </div>

              {log.sandbox?.error != null && (
                <div className="mb-2 flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <JsonViewer data={log.sandbox?.error} className="w-full" />
                </div>
              )}

              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Console output</p>
                <pre className="max-h-40 overflow-auto rounded-md bg-muted/60 p-3 font-mono text-xs leading-relaxed">
                  {log.sandbox?.consoleOutput?.trim() ? log.sandbox.consoleOutput : '(no output)'}
                </pre>
              </div>

              {(log.sandbox?.emits ?? []).length > 0 && (
                <div className="space-y-1.5 pt-2">
                  <p className="text-xs font-medium text-muted-foreground">Sandbox emits</p>
                  <ul className="space-y-1.5">
                    {(log.sandbox?.emits ?? []).map((e, i) => (
                      <EmitRow key={i} event={e} />
                    ))}
                  </ul>
                </div>
              )}
            </Section>

            {/* Deliveries */}
            <Section title="Deliveries" icon={<Hash className="h-4 w-4" />}>
              {(log.emits ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No deliveries were attempted.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50 text-left">
                        <th className="px-3 py-2 font-medium">#</th>
                        <th className="px-3 py-2 font-medium">Kind</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium">Code</th>
                        <th className="px-3 py-2 font-medium">Attempts</th>
                        <th className="px-3 py-2 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(log.emits ?? []).map((e) => (
                        <tr key={e.index} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{e.index}</td>
                          <td className="px-3 py-2 text-xs">{e.kind}</td>
                          <td className="px-3 py-2"><Badge variant="outline">{e.status}</Badge></td>
                          <td className="px-3 py-2 font-mono text-xs">{e.statusCode ?? '—'}</td>
                          <td className="px-3 py-2">{e.attempts}</td>
                          <td className="px-3 py-2">{e.durationMs}ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* Raw log */}
            <Section title="Full record" icon={<BoomBox className="h-4 w-4" />}>
              <JsonViewer data={log} />
            </Section>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MetaField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={cn('truncate text-sm', mono && 'font-mono text-xs')} title={value}>
        {value}
      </span>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        {icon}
        {title}
      </h3>
      {children}
    </div>
  );
}